import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function useCustomMutation<
  TData = unknown,
  TError = unknown,
  TVariables = unknown,
>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?:
    | ((data: TData) => void)
    | { onSuccess?: (data: TData) => void; onError?: (error: TError) => void },
) {
  const queryClient = useQueryClient();
  const opts =
    typeof options === "function" ? { onSuccess: options } : (options ?? {});

  return useMutation<TData, TError, TVariables>({
    mutationFn,
    onSuccess: (data: TData) => {
      if (opts.onSuccess) opts.onSuccess(data);
      queryClient.invalidateQueries(); // Optionally pass query keys to invalidate
    },
    onError: (err: unknown) => {
      if (opts.onError) opts.onError(err as TError);
    },
  });
}

export function useCustomQuery<TData = unknown, TError = unknown>(
  queryKey: ReadonlyArray<unknown>,
  queryFn: () => Promise<TData>,
  options?: { staleTime?: number; enabled?: boolean },
) {
  return useQuery<TData, TError>({
    queryKey,
    queryFn,
    staleTime: options?.staleTime,
    enabled: options?.enabled,
  });
}
